// src/pages/public/About/AboutPage.jsx
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppDispatch.js'
import { fetchPageBySlug } from '../../../redux/slices/cmsSlice.js'
import SEO from '../../../components/seo/SEO.jsx'
import { LazyImage } from '../../../components/common/LazyImage.jsx'

// Make sure your merged styles file is imported
import './index.scss'
import PageCards from '../../../components/common/PageCards.jsx'

export default function AboutPage() {
    const dispatch = useAppDispatch()
    const { currentPage: aboutPage, loading: pageLoading } = useAppSelector((state) => state.cms)
    const navigate = useNavigate();
    useEffect(() => {
        dispatch(fetchPageBySlug('about'))
    }, [dispatch])

    const getSectionData = (sectionKey) => {
        return aboutPage?.sections?.find(section => section.key === sectionKey)?.data
    }

    const firstSection = getSectionData('firstSection')
    const secondSection = getSectionData('secondSection')
    const journeySection = getSectionData('journey')
    const ourValuesSection = getSectionData('ourValues')
    const mindsSection = getSectionData('minds')
    const anotherSection = getSectionData('anotherSection')
    const strategicPartnersSection = getSectionData('ourStrategicPartners')

    // --------- Helpers for values / cards ---------
    const values = ourValuesSection?.values || []
    const anotherCards = anotherSection?.cards || []
    const partners = strategicPartnersSection?.cards || []

    // Journey list -> timeline stages
    const stagesData =
        (journeySection?.list || []).map((item, index) => ({
            capsule: `Milestone ${index + 1}`,
            subheading: item
        })) || []

    if (pageLoading && !aboutPage) {
        // Skeleton fallback
        return (
            <>
                <SEO title="About Us" />
                <div className="space-y-20">
                    {[...Array(4)].map((_, i) => (
                        <section key={i} className="bg-black">
                            <div className="container-custom section-padding">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center animate-pulse">
                                    <div>
                                        <div className="h-8 bg-black-200 rounded w-1/2 mb-6"></div>
                                        <div className="space-y-4">
                                            <div className="h-4 bg-black-200 rounded w-full"></div>
                                            <div className="h-4 bg-black-200 rounded w-5/6"></div>
                                            <div className="h-4 bg-black-200 rounded w-4/6"></div>
                                        </div>
                                    </div>
                                    <div className="h-64 bg-black-200 rounded-lg"></div>
                                </div>
                            </div>
                        </section>
                    ))}
                </div>
            </>
        )
    }

    return (
        <>
            <SEO
                title={aboutPage?.seoTitle || 'About Us'}
                description={aboutPage?.seoDescription}
            />

            {/* 1) HERO – v1 minimal hero UI */}
            <section className="minimal-hero-section-about">
                <div className="minimal-hero-container">
                    {/* Centered small image */}
                    {(firstSection?.iconUrl || firstSection?.imageUrl) && (
                        <motion.div
                            className="center-image-container"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <img
                                src={firstSection.iconUrl || firstSection.imageUrl}
                                alt={firstSection.heading || 'About icon'}
                                className="center-image"
                            />
                        </motion.div>
                    )}

                    {/* Heading */}
                    <motion.h1
                        className="minimal-heading"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <span className="heading-line">
                            {(firstSection?.heading || '').toUpperCase()}
                        </span>
                    </motion.h1>

                    {/* Subheading */}
                    <motion.p
                        className="minimal-subheading"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        {firstSection?.subheading || ''}
                    </motion.p>

                    {/* Button */}
                    {firstSection?.buttonText && (
                        <motion.button
                            className="cta-button"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            onClick={() => navigate("/services")}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            whileHover={{
                                scale: 1.02,
                                boxShadow: '0 15px 35px rgba(0,0,0,0.3)'
                            }}
                        >
                            {firstSection.buttonText}
                        </motion.button>
                    )}
                </div>

                {/* v1: hero had a big image below */}
                {secondSection?.imageUrl && (
                    <motion.img
                        src={secondSection.imageUrl}
                        alt={secondSection.heading || ''}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    />
                )}
            </section>

            {/* 2) CENTERED COPY SECTION */}
            {secondSection && (
                <section className="centered-content">
                    <div className="content-container">
                        <motion.h3
                            className="small-heading"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                        >
                            {secondSection.smallHeading ||
                                secondSection.heading ||
                                ''}
                        </motion.h3>

                        <motion.h2
                            className="big-heading"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                        >
                            {secondSection.heading2 ||
                                secondSection.heading ||
                                ''}
                        </motion.h2>

                        {secondSection.subheading1 && (
                            <motion.p
                                className="paragraph first-para"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.5 }}
                            >
                                {secondSection.subheading1}
                            </motion.p>
                        )}

                        <div className="spacer" />

                        {secondSection.subheading2 && (
                            <motion.p
                                className="paragraph second-para"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.7 }}
                            >
                                {secondSection.subheading2}
                            </motion.p>
                        )}
                    </div>
                </section>
            )}

            <PageCards />

            {/* 3) JOURNEY / PROCESS */}
            {journeySection && journeySection.list && journeySection.list.length > 0 && (
                <section className="process-section">
                    <div className="process-container">
                        <div className="process-grid">
                            {/* Left: Heading + description */}
                            <motion.div
                                className="process-content"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                            >
                                <h2 className="process-heading">
                                    {(journeySection.heading || '').toUpperCase()}
                                </h2>
                                <p className="process-description">
                                    {journeySection.subheading || ''}
                                </p>
                            </motion.div>

                            {/* Right: timeline cards */}
                            <motion.div
                                className="process-timeline"
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <div className="timeline-line" />

                                {stagesData.map((stage, index) => (
                                    <motion.div
                                        key={index}
                                        className="process-card"
                                        initial={{ opacity: 0, x: -30 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                    >
                                        <div className="timeline-point">
                                            <div className="green-point" />
                                        </div>
                                        <div className="card-content">
                                            <span className="process-capsule">
                                                {stage.capsule}
                                            </span>
                                            <h3 className="process-subheading">
                                                {stage.subheading}
                                            </h3>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </div>
                </section>
            )}

            {/* 4) OUR VALUES */}
            {ourValuesSection && values.length > 0 && (
                <section className="results-delivered-section">
                    <div className="results-container">
                        <motion.h2
                            className="section-heading"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            {ourValuesSection.heading || ''}
                        </motion.h2>

                        <motion.div
                            className="results-grid"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            {/* First row */}
                            <div className="results-row">
                                {values.slice(0, 2).map((value, idx) => (
                                    <motion.div
                                        key={idx}
                                        className="result-card"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: 0.1 * (idx + 1) }}
                                        whileHover={{ y: -5 }}
                                    >
                                        <div className="card-image-row">
                                            {value.imageUrl && (
                                                <LazyImage
                                                    src={value.imageUrl}
                                                    alt={value.heading}
                                                    className="result-icon"
                                                    placeholder={
                                                        <div className="result-icon bg-black-200 rounded" />
                                                    }
                                                />
                                            )}
                                        </div>
                                        <div className="card-content-row">
                                            <h3 className="value-title">
                                                {value.heading || `Value ${idx + 1}`}
                                            </h3>
                                            <p className="result-description">
                                                {value.description ||
                                                    value.subheading ||
                                                    ''}
                                            </p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Second row (if more values) */}
                            {values.length > 2 && (
                                <div className="results-row">
                                    {values.slice(2, 4).map((value, idx) => (
                                        <motion.div
                                            key={idx + 2}
                                            className="result-card"
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.5, delay: 0.1 * (idx + 3) }}
                                            whileHover={{ y: -5 }}
                                        >
                                            <div className="card-image-row">
                                                {value.imageUrl && (
                                                    <LazyImage
                                                        src={value.imageUrl}
                                                        alt={value.heading}
                                                        className="result-icon"
                                                        placeholder={
                                                            <div className="result-icon bg-black-200 rounded" />
                                                        }
                                                    />
                                                )}
                                            </div>
                                            <div className="card-content-row">
                                                <h3 className="value-title">
                                                    {value.heading || `Value ${idx + 3}`}
                                                </h3>
                                                <p className="result-description">
                                                    {value.description ||
                                                        value.subheading ||
                                                        ''}
                                                </p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    </div>
                </section>
            )}

            {/* 5) ANOTHER SECTION */}
            {anotherSection && anotherCards.length > 0 && (
                <section className="results-delivered-section">
                    <div className="results-container">
                        <motion.h2
                            className="section-heading"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            {anotherSection.heading || ''}
                        </motion.h2>

                        <motion.div
                            className="results-grid"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            <div className="results-row">
                                {anotherCards.slice(0, 2).map((card, idx) => (
                                    <motion.div
                                        key={idx}
                                        className="result-card"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: 0.1 * (idx + 1) }}
                                        whileHover={{ y: -5 }}
                                    >
                                        <div className="card-image-row">
                                            {card.imageUrl && (
                                                <LazyImage
                                                    src={card.imageUrl}
                                                    alt={card.text || card.heading}
                                                    className="result-icon"
                                                    placeholder={
                                                        <div className="result-icon bg-black-200 rounded" />
                                                    }
                                                />
                                            )}
                                        </div>
                                        <div className="card-content-row">
                                            <p className="result-description">
                                                {card.text || card.subheading || ''}
                                            </p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {anotherCards.length > 2 && (
                                <div className="results-row">
                                    {anotherCards.slice(2, 4).map((card, idx) => (
                                        <motion.div
                                            key={idx + 2}
                                            className="result-card"
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.5, delay: 0.1 * (idx + 3) }}
                                            whileHover={{ y: -5 }}
                                        >
                                            <div className="card-image-row">
                                                {card.imageUrl && (
                                                    <LazyImage
                                                        src={card.imageUrl}
                                                        alt={card.text || card.heading}
                                                        className="result-icon"
                                                        placeholder={
                                                            <div className="result-icon bg-black-200 rounded" />
                                                        }
                                                    />
                                                )}
                                            </div>
                                            <div className="card-content-row">
                                                <p className="result-description">
                                                    {card.text || card.subheading || ''}
                                                </p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </motion.div>

                        {/* Buttons row */}
                        {(anotherSection.primaryButtonText ||
                            anotherSection.secondaryButtonText) && (
                                <div className="buttons-row">
                                    {anotherSection.primaryButtonText && (
                                        <motion.button
                                            className="cta-button"
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.8, delay: 0.6 }}
                                            whileHover={{
                                                scale: 1.02,
                                                boxShadow: '0 15px 35px rgba(0,0,0,0.3)'
                                            }}
                                        >
                                            {anotherSection.primaryButtonText}
                                        </motion.button>
                                    )}
                                    {anotherSection.secondaryButtonText && (
                                        <motion.button
                                            className="cta-button"
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.8, delay: 0.8 }}
                                            whileHover={{
                                                scale: 1.02,
                                                boxShadow: '0 15px 35px rgba(0,0,0,0.3)'
                                            }}
                                        >
                                            {anotherSection.secondaryButtonText}
                                        </motion.button>
                                    )}
                                </div>
                            )}
                    </div>
                </section>
            )}

            {/* 6) MINDS / TEAM */}
            {mindsSection && mindsSection.cards && mindsSection.cards.length > 0 && (
                <div className="team-section-hover">
                    <section className="team-section">
                        <div className="team-section-container">
                            <motion.div
                                className="section-header"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                            >
                                <h2 className="section-heading">
                                    {mindsSection.heading?.toUpperCase() || 'Our Team'}
                                </h2>
                            </motion.div>

                            <motion.div
                                className="partners-grid"
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                {mindsSection.cards.map((member, index) => (
                                    <motion.div
                                        key={index}
                                        className="partner-card"
                                        initial={{ opacity: 0, y: 50 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.8, delay: index * 0.1 }}
                                        whileHover={{ y: -10 }}
                                    >
                                        <div className="partner-image">
                                            {member.imageUrl && (
                                                <motion.img
                                                    src={member.imageUrl}
                                                    alt={member.heading}
                                                    initial={{ scale: 1 }}
                                                    whileHover={{ scale: 1.05 }}
                                                    transition={{ duration: 0.5 }}
                                                />
                                            )}
                                            <div className="parnter-image-content">
                                                {/* <h1>{member.heading}</h1> */}
                                                <h1>Akash Jain</h1>
                                                <span>General Manager</span>
                                                {/* <span>{member.subheading}</span> */}
                                            </div>
                                        </div>

                                        <div className="partner-content">
                                            {/* {member.list && member.list.length > 0 && (
                                                    <ul className="list_1">
                                                        {member.list.map((item, itemIndex) => (
                                                            <li key={itemIndex}>{item}</li>
                                                        ))}
                                                    </ul>
                                                )} */}

                                            {/* {member.description && ( */}
                                            <p className="partner-description">
                                                {/* {member.description} */}
                                                Passionate about building resilient systems that never go down, Akash ensures reliability and excellence at every step.
                                            </p>
                                            {/* )} */}

                                            {/* {member.socialLinks && member.socialLinks.length > 0 && (
                                                <>
                                                    <hr />
                                                    <div className="social-icons">
                                                        {member.socialLinks.map((icon, i) => (
                                                            <span key={i}>
                                                                <img
                                                                    src={icon.imageUrl}
                                                                    onClick={() => { navigate(icon.url) }}
                                                                    alt={`social icon ${i}`}
                                                                />
                                                            </span>
                                                        ))}
                                                    </div>
                                                </>
                                            )} */}

                                            <>
                                                <hr />
                                                <div className="social-icons">
                                                    <span>
                                                        <img
                                                            src={"https://logiqlink-backend-bucket.s3.ap-south-1.amazonaws.com/uploads/1756118737490_Frame_1116607392.svg"}
                                                            onClick={() => { window.open("https://www.linkedin.com/in/akash-jain-indb/") }}
                                                        />
                                                    </span>
                                                </div>
                                            </>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </section>
                </div>
            )}

            {/* 7) STRATEGIC PARTNERS */}
            {strategicPartnersSection && partners.length > 0 && (
                <section className="strategic-partners">
                    <div className="strategic-partners-container">
                        <motion.div
                            className="section-header"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <h2 className="section-heading">
                                {(strategicPartnersSection.heading ||
                                    'Our Strategic Partners'
                                ).toUpperCase()}
                            </h2>
                            {strategicPartnersSection.subheading && (
                                <p className="section-subheading">
                                    {strategicPartnersSection.subheading}
                                </p>
                            )}
                        </motion.div>

                        <motion.div
                            className="partners-grid"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            {partners.map((partner, index) => (
                                <motion.div
                                    key={index}
                                    className="partner-card"
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8, delay: index * 0.1 }}
                                    whileHover={{ y: -10 }}
                                >
                                    <div className="partner-image">
                                        {partner.imageUrl && (
                                            <img
                                                src={partner.imageUrl}
                                                alt={partner.heading}
                                                onError={(e) => {
                                                    e.target.style.display = 'none'
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="partner-content">
                                        <h3 className="partner-heading">
                                            {partner.heading}
                                        </h3>
                                        {partner.subheading && (
                                            <p className="partner-description">
                                                {partner.subheading}
                                            </p>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </section>
            )}
        </>
    )
}